import { useEffect, useRef } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { useTheme } from "../../contexts/ThemeContext";
import { useAuth } from "../../contexts/AuthContext";
import Navbar from "../../components/feature/Navbar";
import AuthForm from "./components/AuthForm";
import DashboardContent from "./components/DashboardContent";
import SetNewPasswordScreen from "./components/SetNewPasswordScreen";

const SUPABASE_FUNCTIONS_URL = import.meta.env.VITE_PUBLIC_SUPABASE_URL
  ? `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1`
  : "";

export default function DashboardPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { user, session, loading, isRecovery, pendingTwoFA } = useAuth();
  const { isDark } = useTheme();

  const intent = searchParams.get("intent");
  const checkoutSession = searchParams.get("session");
  const billingFromStorage = useRef(
    typeof window !== "undefined" ? sessionStorage.getItem("trackly_pro_intent") : null,
  );

  // After sign-in with pro intent, trigger Stripe checkout
  useEffect(() => {
    if (!user || !session) return;

    const storedBilling = billingFromStorage.current;
    const urlIntent = intent;

    if (urlIntent === "pro" || storedBilling) {
      const billing = storedBilling ?? "monthly";
      sessionStorage.removeItem("trackly_pro_intent");
      billingFromStorage.current = null;

      const plan = billing === "lifetime" ? "pro_lifetime" : "pro_monthly";
      const origin = window.location.origin;

      fetch(`${SUPABASE_FUNCTIONS_URL}/stripe-checkout`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          plan,
          successUrl: `${origin}/checkout-success?session={CHECKOUT_SESSION_ID}`,
          cancelUrl: `${origin}/product#pricing`,
        }),
      })
        .then((r) => r.json())
        .then((data) => {
          if (data.url) window.location.href = data.url;
        })
        .catch(() => {
          navigate("/product#pricing-plans");
        });
    }
  }, [user, session, intent, navigate]);

  if (loading) {
    return (
      <div className={`min-h-screen flex items-center justify-center ${isDark ? "bg-[#050505]" : "bg-white"}`}>
        <i className={`ri-loader-4-line animate-spin text-2xl ${isDark ? "text-white/30" : "text-black/20"}`} />
      </div>
    );
  }

  return (
    <div className={`min-h-screen transition-colors duration-300 ${isDark ? "bg-[#050505]" : "bg-white"}`}>
      <Navbar />

      {/* Password recovery flow — shown when user arrives via reset email link */}
      {isRecovery ? (
        <div className="min-h-screen flex items-center justify-center px-6 pt-16">
          <div className="w-full max-w-md">
            <div className="text-center mb-8">
              <img
                src="https://public.readdy.ai/ai/img_res/9279aaa0-a972-448e-b5a8-cead4885da19.png"
                alt="Trackly"
                className={`h-8 w-auto mx-auto mb-6 object-contain ${!isDark ? "invert" : ""}`}
              />
            </div>
            <div className={`rounded-2xl p-8 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
              <SetNewPasswordScreen />
            </div>
          </div>
        </div>
      ) : (!user || pendingTwoFA) ? (
        /* Auth panel */
        <div className="min-h-screen flex items-center justify-center px-6 pt-16">
          <div className="w-full max-w-md">
            {/* Brand */}
            <div className="text-center mb-10">
              <img
                src="https://public.readdy.ai/ai/img_res/9279aaa0-a972-448e-b5a8-cead4885da19.png"
                alt="Trackly"
                className={`h-8 w-auto mx-auto mb-6 object-contain ${!isDark ? "invert" : ""}`}
              />
              <h1 className={`font-syne font-bold text-2xl mb-2 ${isDark ? "text-white" : "text-[#111]"}`}>
                {intent === "pro" ? "Claim your Pro access" : "Your Trackly account"}
              </h1>
              <p className={`font-inter text-sm ${isDark ? "text-[#555]" : "text-[#888]"}`}>
                {intent === "pro"
                  ? "Sign in or create an account to complete your Pro purchase."
                  : "Sign in to view your membership dashboard."}
              </p>
            </div>

            <div className={`rounded-2xl p-8 border ${isDark ? "bg-[#0d0d0d] border-white/6" : "bg-[#f8f8f8] border-black/6"}`}>
              <AuthForm onSuccess={() => {}} intent={intent} />
            </div>
          </div>
        </div>
      ) : (
        /* Dashboard */
        <DashboardContent />
      )}
    </div>
  );
}
